class ProductInfo extends HTMLElement {
  connectedCallback() {
    this.abortController = null;
    this.initVariantPicker();
    this.initQuantityControls();
    this.initGallery();
    this.initZoom();
    this.initGiftRecipient();
    this.initStickyAtc();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.stickyObserver?.disconnect();
  }

  initVariantPicker() {
    this.optionInputs = this.querySelectorAll('[data-option-value]');
    this.optionInputs.forEach((input) => {
      input.addEventListener('change', (event) => this.onOptionChange(event.currentTarget));
    });
  }

  initQuantityControls() {
    this.querySelectorAll('[data-quantity-minus]').forEach((button) => {
      button.addEventListener('click', () => this.changeQuantity(-1));
    });

    this.querySelectorAll('[data-quantity-plus]').forEach((button) => {
      button.addEventListener('click', () => this.changeQuantity(1));
    });
  }

  changeQuantity(delta) {
    const input = this.querySelector('[data-quantity-input]');
    if (!input) return;

    const minimum = Number(input.min || 1);
    const maximum = input.max ? Number(input.max) : Infinity;
    const current = Number(input.value || minimum);
    const next = Math.min(maximum, Math.max(minimum, current + delta));

    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  async onOptionChange(changedInput) {
    const checked = [...this.querySelectorAll('[data-option-value]:checked')];
    const optionCount = Number(this.dataset.optionCount || 0);
    if (checked.length !== optionCount) return;

    const optionValues = checked.map((input) => input.dataset.optionValueId).filter(Boolean);
    if (optionValues.length !== optionCount) return;

    this.abortController?.abort();
    this.abortController = new AbortController();
    this.setAttribute('aria-busy', 'true');

    const targetProductUrl = changedInput?.dataset.productUrl || this.dataset.url;
    const requestUrl = new URL(targetProductUrl, window.location.origin);
    requestUrl.searchParams.set('option_values', optionValues.join(','));
    requestUrl.searchParams.set('section_id', this.dataset.sectionId);

    try {
      const response = await fetch(requestUrl.toString(), {
        signal: this.abortController.signal,
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const incoming = parsed.querySelector('[data-product-info]');
      if (!incoming) throw new Error('No se encontró la sección de producto actualizada.');

      const browserUrl = new URL(targetProductUrl, window.location.origin);
      browserUrl.searchParams.set('option_values', optionValues.join(','));
      browserUrl.searchParams.delete('section_id');

      this.replaceWith(incoming);
      window.history.replaceState({}, '', browserUrl.toString());
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('EconoSmart product variant update failed:', error);
      this.removeAttribute('aria-busy');
    }
  }

  initGallery() {
    const stage = this.querySelector('[data-product-gallery-stage]');
    const slider = this.querySelector('[data-product-gallery-slider]');
    const items = [...this.querySelectorAll('[data-product-media-item]')];
    const thumbnails = [...this.querySelectorAll('[data-product-thumbnail]')];
    const previousButton = this.querySelector('[data-product-gallery-previous]');
    const nextButton = this.querySelector('[data-product-gallery-next]');
    const counter = this.querySelector('[data-product-gallery-counter]');

    if (!slider || items.length === 0) return;

    let activeIndex = 0;
    let scrollFrame = null;

    const setActive = (index, scroll = false) => {
      activeIndex = (index + items.length) % items.length;

      thumbnails.forEach((thumbnail, thumbnailIndex) => {
        const active = thumbnailIndex === activeIndex;
        thumbnail.classList.toggle('is-active', active);
        if (active) thumbnail.setAttribute('aria-current', 'true');
        else thumbnail.removeAttribute('aria-current');
      });

      if (counter) counter.textContent = `${activeIndex + 1} / ${items.length}`;

      if (scroll) {
        slider.scrollTo({ left: items[activeIndex].offsetLeft, behavior: 'smooth' });
      }

      const activeThumbnail = thumbnails[activeIndex];
      activeThumbnail?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    };

    thumbnails.forEach((thumbnail, index) => {
      thumbnail.addEventListener('click', () => setActive(index, true));
    });

    previousButton?.addEventListener('click', () => setActive(activeIndex - 1, true));
    nextButton?.addEventListener('click', () => setActive(activeIndex + 1, true));

    stage?.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        setActive(activeIndex - 1, true);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        setActive(activeIndex + 1, true);
      }
    });

    slider.addEventListener('scroll', () => {
      if (scrollFrame) cancelAnimationFrame(scrollFrame);
      scrollFrame = requestAnimationFrame(() => {
        const index = items.reduce((closestIndex, item, itemIndex) => {
          const currentDistance = Math.abs(item.offsetLeft - slider.scrollLeft);
          const closestDistance = Math.abs(items[closestIndex].offsetLeft - slider.scrollLeft);
          return currentDistance < closestDistance ? itemIndex : closestIndex;
        }, 0);
        setActive(index, false);
      });
    }, { passive: true });

    setActive(0, false);
  }

  initZoom() {
    const dialog = this.querySelector('[data-product-zoom-dialog]');
    if (!dialog) return;

    const image = dialog.querySelector('[data-product-zoom-image]');
    const closeButton = dialog.querySelector('[data-product-zoom-close]');

    this.querySelectorAll('[data-product-zoom]').forEach((button) => {
      button.addEventListener('click', () => {
        if (!image) return;
        image.src = button.dataset.zoomSrc || '';
        image.alt = button.dataset.zoomAlt || '';

        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
      });
    });

    const close = () => {
      if (typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');
    };

    closeButton?.addEventListener('click', close);
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) close();
    });
  }

  initGiftRecipient() {
    const wrapper = this.querySelector('[data-gift-recipient]');
    if (!wrapper) return;

    const toggle = wrapper.querySelector('[data-gift-recipient-toggle]');
    const fieldsWrapper = wrapper.querySelector('[data-gift-recipient-fields]');
    const fields = [...wrapper.querySelectorAll('[data-gift-recipient-field]')];
    const email = wrapper.querySelector('[data-gift-recipient-email]');
    const offset = wrapper.querySelector('[data-gift-recipient-offset]');
    const date = wrapper.querySelector('[data-gift-recipient-date]');

    if (offset) offset.value = String(new Date().getTimezoneOffset());

    if (date) {
      const today = new Date();
      const maxDate = new Date(today);
      maxDate.setDate(maxDate.getDate() + 90);
      date.min = this.formatDate(today);
      date.max = this.formatDate(maxDate);
    }

    const update = () => {
      const enabled = Boolean(toggle?.checked);
      if (fieldsWrapper) fieldsWrapper.hidden = !enabled;
      fields.forEach((field) => { field.disabled = !enabled; });
      if (email) email.required = enabled;
    };

    toggle?.addEventListener('change', update);
    update();
  }

  formatDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  initStickyAtc() {
    const sticky = this.querySelector('[data-product-sticky-atc]');
    const mainButton = this.querySelector('.product-info-panel .product-form__submit');
    if (!sticky || !mainButton || !('IntersectionObserver' in window)) return;

    this.stickyObserver = new IntersectionObserver(([entry]) => {
      sticky.classList.toggle('is-visible', !entry.isIntersecting && window.scrollY > mainButton.getBoundingClientRect().top + window.scrollY - 150);
    }, { threshold: 0.15 });

    this.stickyObserver.observe(mainButton);
  }
}

if (!customElements.get('product-info')) {
  customElements.define('product-info', ProductInfo);
}
